import React from 'react';
import './molecules.css';

interface WelcomeScreenProps {
  participantName?: string;
  organiserName?: string;
  taskCount?: number;
  durationDays?: number;
  welcomeTitle?: string;
  welcomeMessage?: string;
  onContinue: () => void;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  participantName = 'there',
  organiserName,
  taskCount,
  durationDays,
  welcomeTitle,
  welcomeMessage,
  onContinue,
}) => {
  const taskLabel = taskCount ? `${taskCount} tasks` : 'Tasks ready';
  const durationLabel = durationDays
    ? `${durationDays} day${durationDays === 1 ? '' : 's'}`
    : null;
  const tasksTitle = durationLabel ? `${taskLabel} · ${durationLabel}` : taskLabel;
  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundColor: '#6E7A5A',
        minHeight: '100dvh',   // overrides min-h-screen fallback; tracks dynamic browser chrome (Comet/Arc/iOS Safari bottom bar)
      }}
    >
      {/* Molecular decorations */}

      {/* Cluster 2: Bottom left — Arc shape */}
      <div className="mol-bloom-d1" style={{position:'absolute',bottom:55,left:50,pointerEvents:'none'}}>
        <svg width="140" height="70" viewBox="-50 -20 200 90">
          {/* Nodes: gentle curve */}
          <circle cx="0" cy="0" r="5" fill="#fff"/>
          <circle cx="40" cy="-15" r="4" fill="#fff"/>
          <circle cx="80" cy="0" r="5" fill="#fff"/>
          {/* Connections */}
          <line x1="0" y1="0" x2="40" y2="-15" stroke="#fff" strokeWidth="2"/>
          <line x1="40" y1="-15" x2="80" y2="0" stroke="#fff" strokeWidth="2"/>
          {/* 2 waves LEFT from left end */}
          <path d="M-5,0 Q-15,10 -25,0 Q-35,-10 -45,0" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
          {/* 2 waves RIGHT from right end */}
          <path d="M85,0 Q95,-10 105,0 Q115,10 125,0" fill="none" stroke="#1a1814" strokeWidth="3" strokeLinecap="round"/>
        </svg>
      </div>

      {/* Content */}
      <div
        className="relative z-10 px-7"
        style={{
          paddingTop: 'calc(60px + env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {/* Dekko branding */}
        <div className="text-white text-[15px] font-semibold tracking-[0.5px] anim-stagger-1">Dekko</div>

        {/* Label */}
        <div className="mt-7">
          <span className="text-white/60 text-[11px] font-semibold tracking-[2px]">YOU'VE BEEN INVITED</span>
        </div>

        {/* H1 */}
        <h1 className="text-white text-[34px] font-semibold mt-2.5 leading-tight anim-stagger-1">
          {welcomeTitle || <>Welcome,<br/><span className="text-[#7BBFAF]">{participantName}</span></>}
        </h1>

        {welcomeMessage && (
          <p className="text-white/65 text-[15px] mt-3.5 leading-relaxed whitespace-pre-line">
            {welcomeMessage}
          </p>
        )}

        {/* Info cards */}
        <div className="mt-8 flex flex-col gap-3 anim-stagger-2">
          {/* Tasks card */}
          <div className="bg-[#1A1814]/25 rounded-2xl p-4 flex items-center gap-3.5">
            <div className="w-11 h-11 bg-[#D4715A] rounded-xl flex items-center justify-center flex-shrink-0">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="#fff" strokeWidth="2"/>
                <path d="M8 12l3 3 5-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div className="text-white text-[15px] font-semibold">{tasksTitle}</div>
              <div className="text-white/55 text-[13px] mt-0.5">Audio, photo, video & text responses</div>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={onContinue}
          className="w-full mt-7 bg-[#D4715A] text-white rounded-[28px] py-[18px] text-[15px] font-semibold tracking-[1px] flex items-center justify-center gap-2.5 anim-stagger-3"
        >
          LET'S GET STARTED &rarr;
        </button>

        <p className="text-center mt-5 text-white/45 text-[13px]">
          This screen won't appear again
        </p>
      </div>
    </div>
  );
};

export default WelcomeScreen;
